/**
 * Connection to a zone room.
 *
 * The local player is simulated on this machine so movement feels instant, and
 * the server's authoritative result is applied as a correction when the two
 * disagree. Remote players are interpolated rather than snapped, because at
 * 20 ticks a second raw state updates look like stuttering.
 */

import { Client, getStateCallbacks, type Room } from 'colyseus.js';
import type { EnemyView } from '../game/actors.js';

export interface RemotePlayer {
  sessionId: string;
  characterId: string;
  name: string;
  classKey: string;
  level: number;
  away: boolean;
  motion: string;
  appearance: { bodyType: string; hair: string; hairColour: string; skinTone: string; outfit: string };
  /** Where the server last said they were. */
  target: { x: number; y: number; z: number; facing: number };
  /** Where we are drawing them, easing towards the target. */
  render: { x: number; y: number; z: number; facing: number };
}

export interface Correction {
  x: number;
  y: number;
  z: number;
  facing: number;
}

export interface KillReport {
  enemyKey: string;
  name: string;
  experience: number;
  marks: number;
  loot: { itemKey: string; quantity: number }[];
  level: number;
  levelledUp: boolean;
}

export interface CombatMessage {
  kind: 'enemyHit' | 'playerHit' | 'enemyDied' | 'playerDied';
  /** playerDied only: what did it, so the death screen can say. */
  killedBy?: string;
  enemyLevel?: number | null;
  respawnSeconds?: number;
  enemyId?: string;
  sessionId?: string;
  damage?: number;
  by?: string;
}

export interface RoomHandlers {
  onCorrection(correction: Correction): void;
  onZoneChanged(zoneId: string): void;
  onDisconnected(reason: string): void;
  onReconnecting(): void;
  onReconnected(): void;
  onCombat(message: CombatMessage): void;
  onKill(report: KillReport): void;
  onRespawn(at: { x: number; y: number; z: number; facing: number; health: number }): void;
  onChat(message: { id: string; name: string; body: string; at: string }): void;
  onChatHistory(messages: { id: string; name: string; body: string; at: string }[]): void;
  onChatRejected(reason: string): void;
}

/**
 * Where the game socket lives.
 *
 * In production the client and the API are served from one origin, so the
 * page's own host is correct. When they are split — a separate preview server,
 * or a dedicated realtime host later — VITE_GAME_URL points at the game server.
 *
 * This cannot go through a path-prefix proxy: after matchmaking, Colyseus
 * connects to `/<processId>/<roomId>`, which has no fixed prefix to match on.
 */
function endpoint(): string {
  const configured = import.meta.env['VITE_GAME_URL'];
  if (typeof configured === 'string' && configured !== '') return configured;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${window.location.host}`;
}

export class ZoneConnection {
  private client: Client;
  private room: Room | null = null;
  private handlers: RoomHandlers;
  private ownSessionId = '';
  /** Sent only when it changes, so an idle player costs no bandwidth. */
  private lastIntent = '';
  private reportTimer: number | null = null;

  readonly remotes = new Map<string, RemotePlayer>();
  readonly enemies = new Map<string, EnemyView>();

  constructor(handlers: RoomHandlers) {
    this.client = new Client(endpoint());
    this.handlers = handlers;
  }

  get connected(): boolean {
    return this.room !== null;
  }

  get playerCount(): number {
    return this.remotes.size + (this.room !== null ? 1 : 0);
  }

  async join(zoneId: string, accessToken: string, characterId: string): Promise<void> {
    const room = await this.client.joinOrCreate(`zone_${zoneId}`, {
      zoneId,
      accessToken,
      characterId
    });

    this.room = room;
    this.ownSessionId = room.sessionId;
    this.remotes.clear();

    /* State callbacks go through getStateCallbacks in colyseus.js 0.16.
       `room.state.players.onAdd(...)` is the pre-0.16 form and throws
       "Cannot read properties of undefined" here, because the state object is
       not populated until the first patch arrives — the listener has to be
       registered through the callback proxy instead. */
    const $ = getStateCallbacks(room);

    /* The callback proxy's types depend on a fully typed room state, which the
       client does not have — the schema classes live on the server. Rather than
       reach for `any`, describe exactly the shape used below. */
    // Hoisted out of join() so the enemy handlers below can use them too.
    type Fields = Record<string, unknown>;
    interface MapCallbacks {
      onAdd(cb: (item: Fields, key: string) => void): () => void;
      onRemove(cb: (item: Fields, key: string) => void): () => void;
    }
    interface StateCallbacks { players: MapCallbacks }
    const onItem = (item: Fields): { onChange(cb: () => void): () => void } =>
      $(item) as unknown as { onChange(cb: () => void): () => void };

    const state = $(room.state) as unknown as StateCallbacks;

    state.players.onAdd((player: Fields, sessionId: string) => {
      // Our own player is simulated locally; drawing the server copy as well
      // would show a ghost of ourselves a few hundred milliseconds behind.
      if (sessionId === this.ownSessionId) return;
      this.remotes.set(sessionId, this.toRemote(sessionId, player));

      // Per-player changes are subscribed on the player, not the map.
      onItem(player).onChange(() => {
        const existing = this.remotes.get(sessionId);
        if (existing === undefined) return;
        existing.target = {
          x: Number(player['x']), y: Number(player['y']),
          z: Number(player['z']), facing: Number(player['facing'])
        };
        existing.motion = String(player['motion'] ?? 'idle');
        existing.away = player['away'] === true;
        existing.level = Number(player['level'] ?? 1);
      });
    });

    state.players.onRemove((_player: Record<string, unknown>, sessionId: string) => {
      this.remotes.delete(sessionId);
    });

    /* Enemies. Read straight into a plain map rather than interpolated: they
       move on a 20 Hz server tick, and easing them would put the drawn
       position behind where the server says they are — which matters when the
       question is "am I in reach to hit it?". */
    const withEnemies = state as unknown as StateCallbacks & { enemies?: MapCallbacks };
    const readEnemy = (raw: Fields, id: string): EnemyView => ({
      id,
      enemyKey: String(raw['enemyKey'] ?? ''),
      name: String(raw['name'] ?? ''),
      level: Number(raw['level'] ?? 1),
      x: Number(raw['x']), y: Number(raw['y']), z: Number(raw['z']),
      facing: Number(raw['facing']),
      health: Number(raw['health'] ?? 0),
      maxHealth: Number(raw['maxHealth'] ?? 1),
      mode: String(raw['mode'] ?? 'idle'),
      tint: String(raw['tint'] ?? '#8a5a3a')
    });

    withEnemies.enemies?.onAdd((raw: Fields, id: string) => {
      this.enemies.set(id, readEnemy(raw, id));
      onItem(raw).onChange(() => {
        const existing = this.enemies.get(id);
        if (existing === undefined) return;
        Object.assign(existing, readEnemy(raw, id));
      });
    });
    withEnemies.enemies?.onRemove((_raw: Fields, id: string) => this.enemies.delete(id));

    room.onMessage('correction', (message: Correction) => this.handlers.onCorrection(message));
    room.onMessage('combat', (message: CombatMessage) => this.handlers.onCombat(message));
    room.onMessage('kill', (message: KillReport) => this.handlers.onKill(message));
    room.onMessage('respawned', (message: { x: number; y: number; z: number; facing: number; health: number }) =>
      this.handlers.onRespawn(message));
    room.onMessage('chat', (message: { id: string; name: string; body: string; at: string }) =>
      this.handlers.onChat(message));
    room.onMessage('chatHistory', (message: { messages: { id: string; name: string; body: string; at: string }[] }) =>
      this.handlers.onChatHistory(message.messages));
    room.onMessage('chatRejected', (message: { reason?: string }) =>
      this.handlers.onChatRejected(message.reason ?? 'That message was not sent.'));
    room.onMessage('zoneChanged', (message: { zoneId: string }) =>
      this.handlers.onZoneChanged(message.zoneId));

    room.onLeave((code: number) => {
      this.room = null;
      this.remotes.clear();
      this.enemies.clear();
      if (this.reportTimer !== null) window.clearInterval(this.reportTimer);
      // 1000 is a deliberate leave; anything else is a drop worth telling the
      // player about rather than silently freezing the world.
      this.handlers.onDisconnected(code === 1000 ? 'left' : 'dropped');
    });

    // A periodic report of where the client thinks it is. The server compares,
    // and corrects if we have drifted. It is never taken as our position.
    this.reportTimer = window.setInterval(() => {
      if (this.room === null || this.reportPosition === null) return;
      const p = this.reportPosition();
      this.room.send('report', { x: p.x, z: p.z });
    }, 1000);
  }

  /** Set by the game loop so the reporter can read the current local position. */
  reportPosition: (() => { x: number; z: number }) | null = null;

  private toRemote(sessionId: string, player: Record<string, unknown>): RemotePlayer {
    const pos = {
      x: Number(player['x']), y: Number(player['y']),
      z: Number(player['z']), facing: Number(player['facing'])
    };
    return {
      sessionId,
      characterId: String(player['characterId'] ?? ''),
      name: String(player['name'] ?? ''),
      classKey: String(player['classKey'] ?? 'warrior'),
      level: Number(player['level'] ?? 1),
      away: player['away'] === true,
      motion: String(player['motion'] ?? 'idle'),
      appearance: {
        bodyType: String(player['bodyType'] ?? 'average'),
        hair: String(player['hair'] ?? 'short'),
        hairColour: String(player['hairColour'] ?? '#3a2a1c'),
        skinTone: String(player['skinTone'] ?? '#cf9463'),
        outfit: String(player['outfit'] ?? 'plain')
      },
      target: pos,
      render: { ...pos }
    };
  }

  /** Sends movement intent, but only when it has actually changed. */
  sendIntent(intent: { x: number; z: number; running: boolean; jump: boolean; cameraYaw: number }): void {
    if (this.room === null) return;

    // Rounded before comparing, otherwise floating-point noise in a joystick
    // makes every frame look like a change and floods the socket.
    const key = [
      intent.x.toFixed(2), intent.z.toFixed(2),
      intent.running ? '1' : '0', intent.cameraYaw.toFixed(2)
    ].join('|');

    if (key === this.lastIntent && !intent.jump) return;
    this.lastIntent = key;
    this.room.send('intent', intent);
  }

  requestZoneChange(zoneId: string): void {
    this.room?.send('changeZone', { zoneId });
  }

  /** "I attacked" — nothing more. The server picks the target and the damage. */
  attack(): void {
    this.room?.send('attack', {});
  }

  say(body: string): void {
    this.room?.send('chat', { body });
  }

  report(messageId: string, reason = ''): void {
    this.room?.send('reportMessage', { messageId, reason });
  }

  /**
   * Eases remote players towards where the server says they are.
   * Frame-rate independent, so it looks the same at 30 and 120 fps.
   */
  interpolate(dt: number): void {
    const ease = 1 - Math.pow(0.0001, dt);
    for (const remote of this.remotes.values()) {
      remote.render.x += (remote.target.x - remote.render.x) * ease;
      remote.render.y += (remote.target.y - remote.render.y) * ease;
      remote.render.z += (remote.target.z - remote.render.z) * ease;

      // Angles must take the short way round, or a player crossing north spins.
      let delta = (remote.target.facing - remote.render.facing) % (Math.PI * 2);
      if (delta > Math.PI) delta -= Math.PI * 2;
      if (delta <= -Math.PI) delta += Math.PI * 2;
      remote.render.facing += delta * ease;
    }
  }

  async leave(): Promise<void> {
    if (this.reportTimer !== null) window.clearInterval(this.reportTimer);
    await this.room?.leave(true);
    this.room = null;
    this.remotes.clear();
  }
}
